/**
 * POST-AGAINST-SOURCE SUPPORT — the deterministic half of the composer's `check_post_against_source`.
 *
 * Production 2026-10-15 (ITC Tax): the composer wrote five LinkedIn posts from saved sources and all five
 * added claims the source does not make. Raleigh printed 16 moratoriums where the article says 321, and a
 * size threshold changed "and" to "or". The Brain connector has `verify_draft` for this
 * (`server/api/_shared/mcp/verify-draft.ts`); the composer had nothing, so it substituted
 * `run_verification`, which is a campaign checklist and never reads a source.
 *
 * This module answers one question in code, with no model: which checkable things in a POST are absent from
 * the SOURCE it was written from?
 *
 *  1. figures        — "$6M", "40%", "321", "six" must each exist in the source (money and percent compare
 *                      by value and kind, so "$6M" needs six million dollars, not the digit 6),
 *  2. dates          — a month or weekday named in the post must be named in the source ("Tues." = "Tuesday");
 *                      a year is allowed when it is the source's publish year or the current year,
 *  3. names          — a capitalised name (person, place, organisation) must appear in the source,
 *  4. quotations     — a quoted passage must be word for word in the source,
 *  5. connectives    — a figure the source has, written beside "or" where the source has "and" (or the
 *                      reverse) — the Raleigh threshold swap.
 *
 * What it does NOT do: judge claims made in the post's own words. A post that says "the city froze all new
 * approvals" where the source says "paused for six months" passes every check here. `state: 'pass'` means
 * "nothing checkable was missing from the source", never "the post is true". The tool says so in its note.
 *
 * Fails closed on input, not on content: an empty or missing source is `no_source`, never `pass`.
 *
 * Pure: no model, no database. Figure extraction is `unevidenced-figures.ts` (the one copy); date tokens are
 * `protectedFacts` from `article-address-guards.ts`. `verify-draft.ts` (Brain) carries a parallel figure and
 * quote check for library sources; it is deliberately not imported here (it lives under server/ and takes
 * library documents), so the two should be unified when that file is next touched.
 */
import { protectedFacts } from './article-address-guards';
import { extractFigures, moneyOrPercentSpans } from './unevidenced-figures';

export type UnsupportedKind = 'figure' | 'date' | 'name' | 'quote' | 'connective';

export interface UnsupportedClaim {
  /** The sentence of the post that carries the problem, clipped. */
  readonly claim: string;
  /** What is wrong, in words the model can act on. */
  readonly reason: string;
  readonly kind: UnsupportedKind;
}

export interface PostSourceCheck {
  readonly state: 'pass' | 'issues';
  readonly unsupported: UnsupportedClaim[];
}

export interface PostSourceInput {
  readonly post: string;
  /** One or more source texts the post was written from. Absence is judged against all of them together. */
  readonly sources: readonly string[];
  /** Names that may appear without being in the source: the brand, the author. Case-insensitive. */
  readonly allowedTerms?: readonly string[];
  /** The source article's publish date (ISO or any `Date`-parsable string), when known. */
  readonly publishedAt?: string | null;
  /** Clock override for tests; defaults to now. */
  readonly now?: Date;
}

const CLAIM_CHARS = 220;

const clip = (text: string): string => {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= CLAIM_CHARS ? t : `${t.slice(0, CLAIM_CHARS).trimEnd()}…`;
};

const sameValue = (a: number, b: number) =>
  Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

const plainQuotes = (text: string): string => text.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');

interface Word {
  readonly w: string;
  readonly start: number;
  readonly end: number;
}

/** Lowercased words with positions. Punctuation, quote marks and line breaks are ignored on both sides. */
function words(text: string): Word[] {
  return [...plainQuotes(text).matchAll(/[\p{L}\p{N}]+/gu)].map((m) => ({
    w: m[0].toLowerCase(),
    start: m.index!,
    end: m.index! + m[0].length,
  }));
}

const joined = (ws: readonly Word[]) => ` ${ws.map((x) => x.w).join(' ')} `;

/**
 * Abbreviations whose full stop does not end a sentence. Conservative: titles, company suffixes, "St.", "Mt.",
 * "U.S." before a capitalised word, and "No." before a digit. A genuine sentence ending in one of these is
 * merged with its successor, which can only hide a flag inside that one merged sentence, never invent one.
 */
const ABBREVIATION =
  /\b(?:(?:St|Mt|Mr|Mrs|Ms|Dr|Prof|Sr|Jr|Inc|Ltd|Corp|Co|Gen|Sen|Rep|Gov|Lt|Col|Sgt|Capt|Rev|Hon)\.|No\.(?=\s*\d)|U\.S\.(?=\s+\p{Lu}))/gu;
const DOT = '\uE000';

function sentencesOf(text: string): string[] {
  const protectedText = text.replace(ABBREVIATION, (m) => m.replace(/\./g, DOT));
  return protectedText
    .split(/(?<=[.!?])\s+|\n+/)
    .filter((x) => x.trim())
    .map((x) => x.split(DOT).join('.'));
}

/** "1." / "2)" list markers and "Step 3" labels are structure, not figures. */
function stripStructure(sentence: string): string {
  return sentence
    .replace(/^\s*(?:[-*•]\s*)?\d{1,2}[.)](?=\s|$)/, (m) => ' '.repeat(m.length))
    .replace(/\bQ[1-4]\b/g, (m) => ' '.repeat(m.length))
    .replace(/\b24\/7\b/g, (m) => ' '.repeat(m.length))
    .replace(/\b(?:Step|Slide|Page|Section|Part|Option|Phase|Question|Q)\s+\d{1,2}\b/gi, (m) =>
      ' '.repeat(m.length),
    );
}

// ---- 1. figures ----------------------------------------------------------------------------------------

const YEAR = /(?<![\d,.$£€])(?:199\d|20\d\d|2100)(?![\d,]|\.\d|\s?(?:%|percent))/g;

function yearOf(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? undefined : new Date(t).getUTCFullYear();
}

function figureIssues(
  post: string,
  corpus: string,
  allowedYears: ReadonlySet<number>,
): UnsupportedClaim[] {
  const sourceSpans = moneyOrPercentSpans(corpus);
  const sourceFigures = new Set(extractFigures(corpus));
  const out: UnsupportedClaim[] = [];
  const seen = new Set<string>();
  const report = (key: string, figure: string, sentence: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      kind: 'figure',
      claim: clip(sentence),
      reason: `The figure "${figure}" does not appear in the source. Use the source's own figure, or remove it.`,
    });
  };
  for (const sentence of sentencesOf(post)) {
    let rest = stripStructure(sentence).replace(/https?:\/\/\S+/g, (m) => ' '.repeat(m.length));
    for (const span of moneyOrPercentSpans(rest)) {
      rest = rest.slice(0, span.start) + ' '.repeat(span.end - span.start) + rest.slice(span.end);
      const found = sourceSpans.some((s) => s.kind === span.kind && sameValue(s.value, span.value));
      if (!found) report(`${span.kind}:${span.value}`, span.text, sentence);
    }
    // A four-digit year is judged as a year: the source's publish year and the current year are the
    // post's own frame of reference, not a claim the source must make.
    for (const m of [...rest.matchAll(YEAR)]) {
      const year = Number(m[0]);
      rest = rest.slice(0, m.index!) + ' '.repeat(m[0].length) + rest.slice(m.index! + m[0].length);
      if (sourceFigures.has(year) || allowedYears.has(year)) continue;
      const key = `n:${year}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        kind: 'figure',
        claim: clip(sentence),
        reason: `The year "${year}" is a year not in the source. Use the source's year, or remove it.`,
      });
    }
    for (const figure of extractFigures(rest)) {
      if (!sourceFigures.has(figure)) report(`n:${figure}`, String(figure), sentence);
    }
  }
  return out;
}

// ---- 2. dates ------------------------------------------------------------------------------------------

const WEEKDAY_FULL: Readonly<Record<string, string>> = {
  mon: 'Monday',
  tue: 'Tuesday',
  tues: 'Tuesday',
  wed: 'Wednesday',
  weds: 'Wednesday',
  thu: 'Thursday',
  thur: 'Thursday',
  thurs: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};
/** Abbreviations that are also ordinary words ("Sun", "Sat", "Wed", "Mon", "Fri") need a full stop or a date beside them. */
const WEEKDAY_ABBR =
  /\b(Tues?|Thu(?:rs?)?|Weds?|Mon|Fri|Sat|Sun)\b(?:(\.)|(?=,?\s+(?:\d|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\b)))?/g;

/** "Tues." -> "Tuesday", so a source's abbreviation satisfies the post's full name and the reverse. */
export function expandWeekdays(text: string): string {
  return text.replace(WEEKDAY_ABBR, (m, abbr: string, dot: string | undefined) => {
    const lower = abbr.toLowerCase();
    const unambiguous = /^(?:tues?|thu|thur|thurs|weds)$/.test(lower);
    const marked = dot !== undefined || m.length > abbr.length;
    if (!unambiguous && !marked) return m;
    if (!unambiguous && !/^\p{Lu}/u.test(abbr)) return m;
    return WEEKDAY_FULL[lower]!;
  });
}

function dateIssues(rawPost: string, rawCorpus: string): UnsupportedClaim[] {
  const post = expandWeekdays(rawPost);
  const corpus = expandWeekdays(rawCorpus);
  const sourceDates = new Set(
    [...protectedFacts(corpus).keys()].filter((t) => t.startsWith('date:')),
  );
  const out: UnsupportedClaim[] = [];
  const seen = new Set<string>();
  for (const sentence of sentencesOf(post)) {
    for (const token of protectedFacts(sentence).keys()) {
      if (!token.startsWith('date:') || sourceDates.has(token) || seen.has(token)) continue;
      seen.add(token);
      out.push({
        kind: 'date',
        claim: clip(sentence),
        reason: `The date word "${token.slice(5)}" is not in the source. Use the source's date, or leave the date out.`,
      });
    }
  }
  return out;
}

// ---- 3. names ------------------------------------------------------------------------------------------

/** Capitalised words that are not claims: platforms, labels, and words a post capitalises for style. */
const NAME_STOP = new Set(
  [
    'linkedin',
    'instagram',
    'facebook',
    'twitter',
    'threads',
    'tiktok',
    'youtube',
    'pinterest',
    'bluesky',
    'source',
    'sources',
    'read',
    'more',
    'link',
    'comments',
    'thoughts',
    'update',
    'breaking',
    'ps',
    'pps',
    'cta',
    'the',
    'this',
    'that',
    'what',
    'why',
    'how',
    'when',
    'where',
    'who',
    'new',
    'here',
    'today',
    'yes',
    'no',
    'and',
    'but',
    'for',
    'with',
    'from',
    'our',
    'your',
    'their',
    'its',
  ].map((s) => s.toLowerCase()),
);

/**
 * Common English words and title-case call-to-action vocabulary. A capitalised run is a name only if at least
 * one of its tokens is NOT in this list (or the source, or the allowed terms). Deliberately free of common
 * given names and surnames (Will, Mark, Rose, Grace, Bill, Jack) so a person's name is never waved through.
 */
const COMMON_WORDS = new Set(
  (
    'a an the and or but nor so yet if then than as at by for from in into of off on onto out over per to up via with without ' +
    'about after again all also any are be been before between both can could did do does done each every few first get got ' +
    'had has have he her here hers him his how i is it its just last let like make many may me might more most much must my ' +
    'new next no not now only other our ours own same she should some such take that their them these they this those through ' +
    'too under until us use very was we were what when where which while who whom why will would you your yours ' +
    'read full report reports learn key takeaways takeaway download free guide guides join register today tomorrow ' +
    'tonight see watch listen view click tap sign subscribe follow share save apply book start get started now here ' +
    'discover explore find meet visit contact call email message comment comments like repost reply ask tell ' +
    'top best big small great good better why how step steps tip tips lesson lessons rule rules fact facts myth myths ' +
    'update updates news story stories case study studies article articles post posts episode episodes part chapter ' +
    'summary highlights highlight overview details detail result results data insight insights trend trends ' +
    'mr mrs ms dr prof sir madam mayor council member chief director president senator governor judge officer ' +
    'ceo cfo cto coo cmo vp inc llc ltd corp co company team group association department ' +
    'monday tuesday wednesday thursday friday saturday sunday ' +
    'one two three four five six seven eight nine ten ' +
    'yes thanks thank please welcome hello hi hey ' +
    'free live online webinar event events workshop session sessions week month year years day days ' +
    'need needs want wants ready available limited exclusive official official launch'
  )
    .split(/\s+/)
    .filter(Boolean),
);

/** Generic role/format acronyms that are never entity claims. Compared case-insensitively. */
const GENERIC_ACRONYMS = new Set(
  [
    'CEO',
    'CFO',
    'CTO',
    'COO',
    'CMO',
    'CIO',
    'VP',
    'SVP',
    'EVP',
    'HR',
    'PR',
    'IT',
    'AI',
    'ML',
    'API',
    'SaaS',
    'FAQ',
    'FAQS',
    'PDF',
    'ROI',
    'KPI',
    'KPIS',
    'OKR',
    'OKRS',
    'LLC',
    'INC',
    'LTD',
    'USA',
    'US',
    'UK',
    'EU',
    'UN',
    'TV',
    'ID',
    'OK',
    'QA',
    'Q&A',
    'CTA',
    'PS',
    'PPS',
    'AMA',
    'DIY',
    'ETA',
    'TBD',
    'FYI',
    'USD',
    'B2B',
    'B2C',
  ].map((x) => x.toLowerCase()),
);

const ALL_CAPS = /^\p{Lu}[\p{Lu}\p{N}]+$/u;

const MONTH_OR_DAY =
  /^(?:tues?|thurs?|thur|weds?|mon|fri|sat|sun|january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec|monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/i;

const stem = (w: string): string => w.replace(/'s$/, '').replace(/s$/, '');

function nameIssues(
  post: string,
  corpus: string,
  allowed: ReadonlySet<string>,
): UnsupportedClaim[] {
  const sourceWords = new Set(words(corpus).map((x) => x.w));
  const sourceStems = new Set([...sourceWords].map(stem));
  const sourcePhrase = joined(words(corpus));
  const known = (w: string): boolean => {
    const lower = w.toLowerCase().replace(/'s$/, '');
    // The pronoun with a contraction ("I'd", "I'm") is not a name.
    if (/^i'(?:d|m|ve|ll)$/.test(plainQuotes(lower))) return true;
    // Post tokens keep inner "-" and "." ("AI-assisted", "AGENTS.md") while source words split on them, so a
    // compound is known when its parts appear in the same order in the source.
    const parts = lower.split(/[.'-]+/).filter(Boolean);
    if (parts.length > 1 && sourcePhrase.includes(` ${parts.join(' ')} `)) return true;
    return (
      sourceWords.has(lower) ||
      sourceStems.has(stem(lower)) ||
      allowed.has(lower) ||
      NAME_STOP.has(lower) ||
      COMMON_WORDS.has(lower) ||
      COMMON_WORDS.has(stem(lower)) ||
      (ALL_CAPS.test(w) && GENERIC_ACRONYMS.has(lower)) ||
      MONTH_OR_DAY.test(lower)
    );
  };
  const out: UnsupportedClaim[] = [];
  const seen = new Set<string>();
  for (const raw of sentencesOf(post)) {
    // URLs, hashtags, @handles and quoted passages are other checks' business (quotes are verbatim-checked).
    const sentence = plainQuotes(raw)
      .replace(/https?:\/\/\S+/g, ' ')
      .replace(/[#@][\p{L}\p{N}_]+/gu, ' ')
      .replace(/"[^"\n]{3,500}"/g, ' ');
    const tokens = [...sentence.matchAll(/[\p{L}][\p{L}\p{N}'.-]*/gu)].map((m) => ({
      text: m[0].replace(/[.'-]+$/, ''),
      index: m.index!,
    }));
    let i = 0;
    while (i < tokens.length) {
      const first = tokens[i]!;
      const shortCaps = /^\p{Lu}{2}$/u.test(first.text);
      if (!/^\p{Lu}/u.test(first.text) || (first.text.length < 3 && !shortCaps)) {
        i += 1;
        continue;
      }
      // A run of consecutive capitalised words is one name ("Interconnect Capital", "Phillips 66").
      let j = i;
      while (
        j + 1 < tokens.length &&
        /^\p{Lu}/u.test(tokens[j + 1]!.text) &&
        tokens[j + 1]!.text.length >= 2 &&
        /^\s{1}$/.test(
          sentence.slice(tokens[j]!.index + tokens[j]!.text.length, tokens[j + 1]!.index),
        )
      ) {
        j += 1;
      }
      const run = tokens.slice(i, j + 1);
      const atSentenceStart = i === 0;
      // A lone capitalised word opening a sentence is ordinary English ("Here", "Today"); only a multi-word
      // run is treated as a name there.
      const checked = atSentenceStart && run.length === 1 ? [] : run;
      const missing = checked.filter((t) => !known(t.text));
      if (missing.length > 0) {
        const name = run.map((t) => t.text).join(' ');
        const key = name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          out.push({
            kind: 'name',
            claim: clip(raw),
            reason: `The name "${name}" is not in the source. Name only people, places and organisations the source names, or remove it.`,
          });
        }
      }
      i = j + 1;
    }
  }
  return out;
}

// ---- 4. quotations -------------------------------------------------------------------------------------

const MIN_QUOTE_WORDS = 3;

function quoteIssues(post: string, corpus: string): UnsupportedClaim[] {
  const source = joined(words(corpus));
  const out: UnsupportedClaim[] = [];
  // Curly quotes pair unambiguously; straight quotes pair only when they open after a space or line start
  // and close before punctuation or a space, so inch marks never pair.
  const quoteRe = /“([^“”\n]+)”|(?:^|(?<=[\s(]))"([^"\n]+)"(?=[\s.,;:!?)]|$)/gm;
  for (const match of post.matchAll(quoteRe)) {
    const quoted = (match[1] ?? match[2])!;
    if (words(quoted).length < MIN_QUOTE_WORDS) continue;
    const parts = quoted
      .split(/\.\.\.|…/)
      .map((p) => joined(words(p)))
      .filter((p) => p.trim());
    if (!parts.every((p) => source.includes(p))) {
      out.push({
        kind: 'quote',
        claim: clip(quoted),
        reason:
          'This quotation is not word for word in the source. Quote the source exactly, or drop the quotation marks and paraphrase.',
      });
    }
  }
  return out;
}

// ---- 5. connectives beside a figure -------------------------------------------------------------------

const WINDOW_WORDS = 6;

/** The and/or connectives within a few words of each occurrence of a figure, one set per occurrence. */
function connectivesAround(text: string, figure: number): Array<Set<'and' | 'or'>> {
  const ws = words(text);
  const positions: number[] = [];
  for (const m of text.matchAll(/\d[\d,]*(?:\.\d+)?/g)) {
    if (sameValue(Number(m[0].replace(/,/g, '')), figure)) positions.push(m.index!);
  }
  for (const w of ws) {
    if (w.w in SPELLED_FIGURES && sameValue(SPELLED_FIGURES[w.w]!, figure)) positions.push(w.start);
  }
  return positions.map((pos) => {
    const at = ws.findIndex((w) => w.end > pos);
    const found = new Set<'and' | 'or'>();
    if (at < 0) return found;
    for (
      let k = Math.max(0, at - WINDOW_WORDS);
      k <= Math.min(ws.length - 1, at + WINDOW_WORDS);
      k += 1
    ) {
      if (ws[k]!.w === 'and' || ws[k]!.w === 'or') found.add(ws[k]!.w as 'and' | 'or');
    }
    return found;
  });
}

const SPELLED_FIGURES: Readonly<Record<string, number>> = {
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  hundred: 100,
  thousand: 1000,
};

function connectiveIssues(post: string, corpus: string): UnsupportedClaim[] {
  const sourceFigures = new Set(extractFigures(corpus));
  const out: UnsupportedClaim[] = [];
  const seen = new Set<string>();
  for (const sentence of sentencesOf(post)) {
    for (const figure of new Set(extractFigures(sentence))) {
      if (!sourceFigures.has(figure)) continue; // an absent figure is already a figure issue
      const postWindows = connectivesAround(sentence, figure);
      const sourceWindows = connectivesAround(corpus, figure);
      if (sourceWindows.length === 0) continue;
      for (const pw of postWindows) {
        for (const connective of pw) {
          const other = connective === 'and' ? 'or' : 'and';
          const sourceHasSame = sourceWindows.some((sw) => sw.has(connective));
          const sourceHasOther = sourceWindows.some((sw) => sw.has(other));
          if (sourceHasSame || !sourceHasOther) continue;
          const key = `${figure}:${connective}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push({
            kind: 'connective',
            claim: clip(sentence),
            reason: `Beside the figure ${figure} the post says "${connective}" where the source says "${other}". That changes a condition or threshold. Restore the source's "${other}".`,
          });
        }
      }
    }
  }
  return out;
}

// ---- entry ---------------------------------------------------------------------------------------------

/** Longest source text considered, so a pasted argument or a huge document cannot stall a turn. */
export const MAX_SOURCE_CHARS = 120_000;

export function checkPostAgainstSource(input: PostSourceInput): PostSourceCheck {
  const corpus = input.sources.map((s) => s.slice(0, MAX_SOURCE_CHARS)).join('\n\n');
  const allowed = new Set(
    (input.allowedTerms ?? []).flatMap((term) =>
      term
        .toLowerCase()
        .split(/[^\p{L}\p{N}']+/u)
        .filter(Boolean),
    ),
  );
  const now = input.now ?? new Date();
  const allowedYears = new Set<number>([now.getUTCFullYear()]);
  const publishYear = yearOf(input.publishedAt);
  if (publishYear !== undefined) allowedYears.add(publishYear);
  const unsupported = [
    ...figureIssues(input.post, corpus, allowedYears),
    ...connectiveIssues(input.post, corpus),
    ...dateIssues(input.post, corpus),
    ...quoteIssues(input.post, corpus),
    ...nameIssues(input.post, corpus, allowed),
  ];
  return { state: unsupported.length === 0 ? 'pass' : 'issues', unsupported };
}
